import { Box } from '@mui/material'
import { useCallback, useMemo, useRef } from 'react'

import type { BidLadderForecastData } from '@/features/bidding/hooks/useBidLadderForecast'
import useChartContainerWidth, { MIN_CHART_WIDTH } from '@/features/bidding/hooks/useChartContainerWidth'
import { useD3BidLadder } from '@/features/bidding/hooks/useD3BidLadder'
import type { BidLadderChartProps, BidLevel, ChartDimensions } from '@/features/bidding/types/bidLadder'
import { CHART_MARGIN } from '@/features/bidding/utils/cascading/chartConstants'

interface BidLadderOverviewChartProps extends Omit<BidLadderChartProps, 'bidLevels' | 'forecastData'> {
  bidLevels?: BidLevel[]
  height?: number
  primaryForecastData?: BidLadderForecastData
  referenceForecastData?: BidLadderForecastData
  /** Daily opportunity cost value (sum of hourly values) */
  opportunityCost?: number
}

// Calculate legend rows needed based on available width
function calculateLegendRows(chartWidth: number, legendItems: string[]): number {
  const lineLength = 30
  const gap = 8
  const itemGap = 20
  const minLeftMargin = 10

  let currentX = chartWidth
  let rows = 1

  for (const name of legendItems) {
    const estimatedItemWidth = lineLength + gap + name.length * 7 + itemGap

    if (currentX - estimatedItemWidth < minLeftMargin && currentX < chartWidth) {
      rows++
      currentX = chartWidth
    }
    currentX -= estimatedItemWidth
  }

  return rows
}

function BidLadderOverviewChart({
  bidLevels = [],
  expectedDemandMW = 270,
  avgClearingPrice = 42,
  clearingRangeLow = 35,
  clearingRangeHigh = 50,
  totalMWAvailable = 400,
  height: propHeight = 500,
  primaryForecastData,
  referenceForecastData,
  opportunityCost,
  onChartClick,
  onChartRightClick,
}: Readonly<BidLadderOverviewChartProps>) {
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const containerWidth = useChartContainerWidth(chartContainerRef, { throttleMs: 10 })

  const baseChartHeight = propHeight - 20 - 80 // Content area height stays constant (propHeight minus base top/bottom margins)

  // Calculate dynamic margins based on legend wrapping needs
  const calculateDimensions = useCallback(
    (width: number): ChartDimensions => {
      const baseMargin = CHART_MARGIN
      const rowHeight = 24

      // Calculate chart width (width minus left/right margins)
      const chartWidth = width - baseMargin.left - baseMargin.right

      // All legend items go to bottom now
      const bottomLegendItems: string[] = []
      if (opportunityCost !== undefined) bottomLegendItems.push('Opportunity Cost')
      if (primaryForecastData) bottomLegendItems.push('Clearing Range Up')
      if (referenceForecastData) bottomLegendItems.push('Clearing Range Down')
      const bottomRows = calculateLegendRows(chartWidth, bottomLegendItems)

      // Add extra margins for additional rows (first row is already accounted for in base margin)
      const extraBottomMargin = (bottomRows - 1) * rowHeight
      const margin = {
        ...baseMargin,
        bottom: baseMargin.bottom + extraBottomMargin,
      }

      // Total height = content area + dynamic margins
      const height = baseChartHeight + margin.top + margin.bottom

      return { width, height, margin }
    },
    [baseChartHeight, primaryForecastData, referenceForecastData, opportunityCost],
  )

  // Calculate dimensions using measured container width
  const dimensions = useMemo(
    () => calculateDimensions(containerWidth ?? MIN_CHART_WIDTH),
    [calculateDimensions, containerWidth],
  )

  // Prepare chart props for the hook
  const chartProps: BidLadderChartProps = useMemo(
    () => ({
      bidLevels,
      expectedDemandMW,
      avgClearingPrice,
      clearingRangeLow,
      clearingRangeHigh,
      totalMWAvailable,
      primaryForecastData,
      referenceForecastData,
      showClearingRanges: true,
      showP80Range: true,
      showP50Range: true,
      showOpportunityCosts: opportunityCost !== undefined,
      opportunityCost,
      onChartClick,
      onChartRightClick,
    }),
    [
      bidLevels,
      expectedDemandMW,
      avgClearingPrice,
      clearingRangeLow,
      clearingRangeHigh,
      totalMWAvailable,
      primaryForecastData,
      referenceForecastData,
      opportunityCost,
      onChartClick,
      onChartRightClick,
    ],
  )

  // Use the D3 hook to render the chart
  useD3BidLadder(chartContainerRef, chartProps, dimensions)

  return (
    <Box
      ref={chartContainerRef}
      sx={{
        width: '100%',
        height: propHeight,
        '& svg': {
          display: 'block',
        },
      }}
    />
  )
}

export default BidLadderOverviewChart
